import type { VisitorProfileField } from "@/lib/graphql/queries/generated/profile.generated";

/**
 * Per-field value validators surfaced to the ProfileTab. Each returns a
 * symbolic kind (mapped to an i18n key by the caller) or `null` when
 * the value is acceptable. Validators are intentionally NON-fatal for
 * empty input — `required` is enforced separately so the messaging
 * doesn't get crossed (`"required"` is a different concern from
 * `"this email is malformed"`).
 *
 * Kept as a single dispatch table keyed by `field.key` so adding a new
 * recognised key (e.g. "url") is one entry, not a switch sprawl.
 */
export type FieldValidationKind = "invalidEmail" | "invalidPhone";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(value: string): FieldValidationKind | null {
  if (!value) return null;
  return EMAIL_RE.test(value.trim()) ? null : "invalidEmail";
}

/** Accepts E.164 (`+380…`) or national format with a sensible default
 *  region. We default to `undefined` so libphonenumber's E.164 mode is
 *  enforced — accepting national-only numbers without a region would
 *  silently let bad inputs through. */
let isValidPhoneNumber: ((value: string) => boolean) | null = null;

/**
 * Load libphonenumber (~120 KB) on demand so it stays out of the launcher's download. The Profile
 * tab calls this on mount and `handleSave` awaits it, so the real check always runs before a save.
 * A failed load (offline) resolves anyway: the `+` prefix check still applies and the BE validates.
 */
export async function ensurePhoneValidation(): Promise<void> {
  if (isValidPhoneNumber) return;
  try {
    isValidPhoneNumber = (await import("libphonenumber-js/min")).isValidPhoneNumber;
  } catch (error) {
    // Keep the lenient fallback; retried on the next call.
    console.warn(
      '[wexio] the "phone-validation" chunk failed to load; only the "+" check applies',
      error,
    );
  }
}

export function validatePhone(value: string): FieldValidationKind | null {
  if (!value) return null;
  const trimmed = value.trim();
  // libphonenumber rejects leading-zero national numbers without a
  // region; require the `+` prefix so we always validate E.164.
  if (!trimmed.startsWith("+")) return "invalidPhone";
  return !isValidPhoneNumber || isValidPhoneNumber(trimmed) ? null : "invalidPhone";
}

/** Dispatch a value through the right validator for a BE-typed field.
 *  Returns `null` when the value is acceptable (or no validator applies). */
export function validateFieldValue(
  field: VisitorProfileField,
  value: string | null,
): FieldValidationKind | null {
  if (value == null || value === "") return null;
  // Match on canonical field keys the BE surfaces for stock identity
  // fields; operator-defined keys fall through and only get the BE's
  // server-side validation.
  if (field.key === "email") return validateEmail(value);
  if (field.key === "phone") return validatePhone(value);
  return null;
}
